import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Animated,
  Dimensions,
  Modal,
  Pressable,
  ColorValue,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

interface FABOption {
  id: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  color: ColorValue;
  onPress: () => void;
}

interface FloatingActionButtonProps {
  options: FABOption[];
  mainIcon?: keyof typeof Ionicons.glyphMap;
  mainColor?: ColorValue;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'inline';
}

export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  options,
  mainIcon = 'add',
  mainColor = colors.primary,
  position = 'bottom-right',
}) => {
  const styles = useThemedStyles(getStyles);
  const [isExpanded, setIsExpanded] = useState(false);
  const [animation] = useState(new Animated.Value(0));

  const toggleExpanded = () => {
    const toValue = isExpanded ? 0 : 1;
    setIsExpanded(!isExpanded);
    
    Animated.spring(animation, {
      toValue,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();
  };

  const handleOptionPress = (onPress: () => void) => {
    onPress();
    toggleExpanded();
  };

  const getPositionStyle = () => {
    const baseStyle = {
      position: 'absolute' as const,
      bottom: spacing.xl,
    };

    switch (position) {
      case 'bottom-right':
        return { ...baseStyle, right: spacing.xl };
      case 'bottom-left':
        return { ...baseStyle, left: spacing.xl };
      case 'top-right':
        return { ...baseStyle, top: spacing.xl, right: spacing.xl, bottom: undefined };
      case 'top-left':
        return { ...baseStyle, top: spacing.xl, left: spacing.xl, bottom: undefined };
      case 'inline':
        return { position: 'relative' as const, bottom: 0, right: 0, left: 0 };
      default:
        return { ...baseStyle, right: spacing.xl };
    }
  };

  const renderOptions = () => {
    if (!options || !Array.isArray(options) || options.length === 0) {
      return null;
    }

    if (position === 'inline') {
      // For inline position, render options horizontally to the left of the main button
      return options.map((option, index) => {
        const translateX = animation.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -(index + 1) * 60], // Slide left instead of down
        });

        const opacity = animation.interpolate({
          inputRange: [0, 0.5, 1],
          outputRange: [0, 0, 1],
        });

        return (
          <Animated.View
            key={option.id}
            style={[
              styles.inlineOptionContainer,
              {
                transform: [{ translateX }],
                opacity,
              },
            ]}
          >
            <TouchableOpacity
              style={[styles.optionButton, { backgroundColor: option.color }]}
              onPress={() => handleOptionPress(option.onPress)}
              activeOpacity={0.8}
            >
              <Ionicons name={option.icon} size={24} color="white" />
            </TouchableOpacity>
          </Animated.View>
        );
      });
    }

    // For other positions, use the original vertical layout
    return options.map((option, index) => {
      const translateY = animation.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -(index + 1) * 70],
      });

      const opacity = animation.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [0, 0, 1],
      });

      return (
        <Animated.View
          key={option.id}
          style={[
            styles.optionContainer,
            {
              transform: [{ translateY }],
              opacity,
            },
          ]}
        >
          <TouchableOpacity
            style={[styles.optionButton, { backgroundColor: option.color }]}
            onPress={() => handleOptionPress(option.onPress)}
            activeOpacity={0.8}
          >
            <Ionicons name={option.icon} size={24} color="white" />
          </TouchableOpacity>
          <Text style={styles.optionLabel}>{option.label}</Text>
        </Animated.View>
      );
    });
  };

  const mainButtonRotation = animation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '45deg'],
  });

  return (
    <View style={[
      styles.container, 
      getPositionStyle(), 
      position === 'inline' && styles.inlineContainer,
      Platform.OS === 'web' && position === 'inline' && styles.webInlineContainer
    ]}>
      {isExpanded && position !== 'inline' && (
        <Pressable
          style={styles.backdrop}
          onPress={toggleExpanded}
        />
      )}
      
      {renderOptions()}
      
      <TouchableOpacity
        style={[styles.mainButton, { backgroundColor: mainColor }]}
        onPress={toggleExpanded}
        activeOpacity={0.8}
      >
        <Animated.View
          style={{
            transform: [{ rotate: mainButtonRotation }],
          }}
        >
          <Ionicons name={mainIcon} size={28} color="white" />
        </Animated.View>
      </TouchableOpacity>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    alignItems: 'center',
    zIndex: Platform.OS === 'web' ? 999999 : 9999,
  },
  inlineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: Platform.OS === 'web' ? 999999 : 9999,
    position: 'relative',
  },
  webInlineContainer: {
    position: 'relative',
    zIndex: 999999,
  },
  backdrop: {
    position: 'absolute',
    top: -screenHeight,
    left: -screenWidth,
    width: screenWidth * 2,
    height: screenHeight * 2,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: -1,
  },
  mainButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    zIndex: Platform.OS === 'web' ? 1000001 : 10002,
  },
  optionContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    alignItems: 'center',
  },
  inlineOptionContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
    alignItems: 'center',
    zIndex: Platform.OS === 'web' ? 999999 : 10000,
  },
  optionButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    zIndex: Platform.OS === 'web' ? 1000000 : 10001,
  },
  optionLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginTop: spacing.xs,
    textAlign: 'center',
    minWidth: 80,
  },
});
